/*
 * Expanding Search Bar
 * 'width' prop adjusts the search input. defaults to 'auto';
 * 'margin' props adjust the searchBar container
 */
import debounce from "common/utils/debounce";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import cx from "ui/cx";
import IconClose from "ui/icons/IconClosePortal";
import IconSearch from "ui/icons/IconSearchPortal";
import styles from "./SearchBar.module.scss";

interface SearchBarProps {
  handleChange: (searchVal: string) => void;
  id: string;
  marginLeft?: number;
  marginRight?: number;
  placeholder?: string;
  reset?: boolean;
  width?: number;
  hide?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  onClose?: () => void;
}

export default function SearchBar({
  handleChange,
  id,
  marginLeft = 6,
  marginRight = 6,
  placeholder = "Search",
  width,
  reset,
  hide,
  onFocus,
  onBlur,
  onClose,
}: SearchBarProps) {
  const [searchStr, setSearchStr] = useState("");
  const elemRef = useRef<HTMLDivElement>(null);

  const debouncedHandleChange = useMemo(() => debounce(handleChange, 200), [handleChange]);

  //useful when parent needs to reset the searchBar
  useEffect(() => {
    if (reset) {
      setSearchStr("");
    }
  }, [reset]);

  if (elemRef.current) {
    const style = elemRef.current.style;
    const focusWidth = width !== undefined ? `${width}px` : "auto";
    style.setProperty("--input-focus-width", focusWidth);
    style.setProperty("--margin-left", `${marginLeft}px`);
    style.setProperty("--margin-right", `${marginRight}px`);
  }

  //note use of 'mouseDown' vs 'click' in order to avoid event hijacking by 'focus'
  const handleClose = () => {
    setSearchStr("");
    handleChange(""); // immediate clear, bypass debounce
    onClose?.();
  };

  const handleChangeLocal = (evt: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = evt.target.value;
    setSearchStr(searchTerm);
    debouncedHandleChange(searchTerm);
  };

  return (
    <div className={cx(styles.wrapper, "search-bar")} style={hide ? { visibility: "hidden" } : {}} ref={elemRef}>
      <input
        className={`search py-2 ${!!searchStr ? "simulate-focus" : ""}`}
        id={id}
        type="search"
        placeholder={placeholder}
        onChange={handleChangeLocal}
        value={searchStr}
        onFocus={() => onFocus?.()}
        onBlur={() => onBlur?.()}
      />
      <label className="button search-button" htmlFor={id}>
        <IconSearch size={20} color="stone" />
        <span>Search</span>
      </label>

      {!!searchStr && (
        <span className="clear" onMouseDown={handleClose}>
          <IconClose />
        </span>
      )}
    </div>
  );
}
